// src/store/index.js
import { create } from 'zustand';
import { authAPI, adminAPI, accountsAPI, groupsAPI, postsAPI } from '../utils/api';

export const useStore = create((set, get) => ({
  // Auth — the session lives in an httpOnly cookie; `user` is filled by checkAuth()
  // on startup (GET /auth/me) and by login. Registering doesn't sign in: the account
  // waits for email verification and the superadmin's approval.
  user: null,
  authChecked: false,

  checkAuth: async () => {
    try {
      const res = await authAPI.me();
      set({ user: res.data.user, authChecked: true });
    } catch {
      set({ user: null, authChecked: true });
    }
  },
  login: async (email, password) => {
    const res = await authAPI.login({ email, password });
    set({ user: res.data.user });
    return res.data;
  },
  register: async (data) => {
    const res = await authAPI.register(data);
    return res.data;
  },
  setUser: (user) => set({ user }),
  logout: async (everywhere = false) => {
    try { await (everywhere ? authAPI.logoutAll() : authAPI.logout()); } catch { /* already signed out */ }
    // Earlier builds kept the JWT in localStorage; clear any leftovers.
    try { localStorage.removeItem('pf_token'); localStorage.removeItem('pf_user'); } catch {}
    set({ user: null, accounts: [], groups: [], posts: [], adminPending: 0 });
  },

  // Superadmin: organizations awaiting approval (sidebar badge)
  adminPending: 0,
  fetchAdminPending: async () => {
    try {
      const res = await adminAPI.stats();
      set({ adminPending: res.data.stats.awaiting_approval });
    } catch { /* not critical */ }
  },

  // Accounts
  accounts: [],
  accountsLoading: false,
  fetchAccounts: async (params) => {
    set({ accountsLoading: true });
    try {
      const res = await accountsAPI.list(params);
      set({ accounts: res.data.accounts });
    } finally { set({ accountsLoading: false }); }
  },
  reconnectAccount: async (id) => {
    await accountsAPI.reconnect(id);
    get().fetchAccounts();
  },
  deleteAccount: async (id) => {
    await accountsAPI.delete(id);
    set(s => ({ accounts: s.accounts.filter(a => a.id !== id) }));
  },

  // Groups
  groups: [],
  groupsLoading: false,
  fetchGroups: async (params) => {
    set({ groupsLoading: true });
    try {
      const res = await groupsAPI.list(params);
      set({ groups: res.data.groups });
    } finally { set({ groupsLoading: false }); }
  },
  createGroup: async (data) => {
    const res = await groupsAPI.create(data);
    get().fetchGroups();
    return res.data.group;
  },
  deleteGroup: async (id) => {
    await groupsAPI.delete(id);
    set(s => ({ groups: s.groups.filter(g => g.id !== id) }));
  },

  // Posts
  posts: [],
  postsTotal: 0,
  postsLoading: false,
  postsSummary: { queued: 0, delivered_30: 0, drafts: 0, errors: 0, pending_review: 0 },
  fetchPosts: async (params) => {
    set({ postsLoading: true });
    try {
      const res = await postsAPI.list(params);
      set({ posts: res.data.posts, postsTotal: res.data.total });
    } finally { set({ postsLoading: false }); }
  },
  fetchSummary: async () => {
    const res = await postsAPI.summary();
    set({ postsSummary: res.data.summary });
  },
  createPost: async (data) => {
    const res = await postsAPI.create(data);
    get().fetchSummary();
    return res.data.post;
  },
  deletePost: async (id) => {
    await postsAPI.delete(id);
    set(s => ({ posts: s.posts.filter(p => p.id !== id) }));
    get().fetchSummary();
  },
}));
